/**
 * Displays the shopper's remembered iPhone model in model-aware theme bars.
 *
 * DOM contract: the custom element contains a `[data-model-bar-text]` node and
 * may contain a `[data-model-change]` button. That button's label switches
 * between its `data-choose-text` ("Choose", shown while no model is set) and
 * `data-change-text` ("Change", once one is) so the CTA never claims there is
 * something to "change" before anything has been chosen. It reads
 * `vellum:selectedModel` from localStorage and listens for
 * `vellum:model-selected`. An instance with
 * a `data-hide-when-empty` attribute renders nothing at all (instead of an
 * empty-state placeholder line) until a model actually exists - used on the
 * product page, where the model picker is already shown inline in the form
 * so a redundant "which iPhone?" bar above it would be noise.
 * Accessibility: visible text updates only; controls remain native buttons.
 */
class VellumModelBar extends HTMLElement {
  connectedCallback() {
    this.textTarget = this.querySelector('[data-model-bar-text]');
    this.changeButton = this.querySelector('[data-model-change]');
    this.selectedTemplate = this.dataset.selectedTemplate || '';
    this.emptyTemplate = this.dataset.emptyTemplate || '';
    this.render();

    this.changeButton?.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('vellum:model-change-requested'));
      // Prefer the homepage/collection hero model-finder when it exists on
      // the page; otherwise (e.g. the product page, which has no
      // vellum-model-selector) fall back to this product's own inline
      // model fieldset so the button is never a dead click.
      const selector = document.querySelector('vellum-model-selector') || document.querySelector('[data-model-option]');
      selector?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const focusTarget = selector?.querySelector('[data-model], [data-model-value]');
      focusTarget?.focus({ preventScroll: true });
    });
    window.addEventListener('vellum:model-selected', this.render.bind(this));
  }

  render() {
    const model = window.localStorage.getItem('vellum:selectedModel');

    if (!model && this.hasAttribute('data-hide-when-empty')) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const text = model
      ? this.selectedTemplate.replace('[model]', model)
      : this.emptyTemplate;

    if (this.textTarget) {
      this.textTarget.textContent = text;
    }

    if (this.changeButton) {
      const label = model ? this.changeButton.dataset.changeText : this.changeButton.dataset.chooseText;
      if (label) this.changeButton.textContent = label;
    }
  }
}

if (!customElements.get('vellum-model-bar')) {
  customElements.define('vellum-model-bar', VellumModelBar);
}
